'use client';

import { create } from 'zustand';
import type {
  AssistantAction,
  AssistantHighlightElement,
  AssistantSettingsSection,
} from '@/types/assistant';

/**
 * The seam between the AI architect panel (`features/assistant`) and the
 * screens it drives (docs/assistant/ADR-001 §4, §5). It lives in `shared/` so
 * the design features can publish their own buttons without importing the
 * assistant, and the assistant can call them without importing the design
 * features' internals.
 *
 * Two kinds of hooks are published here:
 *
 * - **Targets**: interface elements the API may name in `highlight` and
 *   `open_settings_panel`. A screen marks its element with
 *   `data-assistant-target={assistantTargets(...)}` (a space-separated list,
 *   so one row can be both a settings section and a highlight element); the
 *   assistant finds it in
 *   the DOM, scrolls it into view and, for a settings section, presses it, so
 *   the section opens through its own row handler.
 * - **Actions**: the paid actions an `action_card` proposes. The component that
 *   owns the regular button registers the very handler that button calls, keyed
 *   by the record it acts on (design, estimate). The card's primary button calls
 *   it. A card whose screen is not mounted yet waits for the registration (the
 *   assistant navigates there first).
 */

export type AssistantTarget =
  | `section:${AssistantSettingsSection}`
  | `element:${AssistantHighlightElement}`;

export function sectionTarget(name: AssistantSettingsSection): AssistantTarget {
  return `section:${name}`;
}

export function elementTarget(
  name: AssistantHighlightElement
): AssistantTarget {
  return `element:${name}`;
}

/** The `data-assistant-target` value for one element (one or more targets). */
export function assistantTargets(...targets: AssistantTarget[]): string {
  return targets.join(' ');
}

/** Elements on screen carrying the target, in DOM order. */
export function findAssistantTargets(target: AssistantTarget): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-assistant-target~="${target}"]`
    )
  ).filter((el) => el.getClientRects().length > 0);
}

type ActionHandler = () => void | Promise<void>;

interface AssistantActionsState {
  /** `${action}:${scopeId}` → the handler the regular button calls. */
  handlers: Record<string, ActionHandler>;
  register: (key: string, handler: ActionHandler) => void;
  unregister: (key: string, handler: ActionHandler) => void;
}

export const assistantActionKey = (
  action: AssistantAction,
  scopeId: string
): string => `${action}:${scopeId}`;

export const useAssistantActionsStore = create<AssistantActionsState>(
  (set) => ({
    handlers: {},
    register: (key, handler) =>
      set((state) => ({ handlers: { ...state.handlers, [key]: handler } })),
    // Only the registration that is still current may remove itself: on a
    // remount the new instance registers before the old one's cleanup runs.
    unregister: (key, handler) =>
      set((state) => {
        if (state.handlers[key] !== handler) return state;
        const rest = { ...state.handlers };
        delete rest[key];
        return { handlers: rest };
      }),
  })
);
